"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.addMetric = void 0;
exports.applyAnalyticDefinitionUpdatersForNewVersion = applyAnalyticDefinitionUpdatersForNewVersion;
exports.updateMetricCalculationDescription = exports.removeMetric = exports.deprecateMetric = void 0;
/**
 * applyAnalyticDefinitionUpdatersForNewVersion - apply the given `analyticsChangesToApply` (immutably) to the `analyticsVersionToUpdate` to create a new version of the analytics description.
 *
 * @param {AnalyticsVersionDescription} analyticsVersionToUpdate - the description to update (Immutably)
 * @param {boolean} canCompareWithPreviousVersion - true if the changes still allow these analytics metrics to be comparable to the previous version of the analytics metrics
 * @param {Array<AnalyticsVersionUpdater>} analyticsChangesToApply - list of updates to apply to the `analyticsVersionToUpdate`
 *
 * @returns {AnalyticsVersionDescription} - an updated version of `analyticsVersionToUpdate` with all the updates applied
 */
function applyAnalyticDefinitionUpdatersForNewVersion(analyticsVersionDescriptionToUpdate, canCompareWithPreviousVersion, analyticsChangesToApply) {
  let currentAnalyticsDescription = {
    ...analyticsVersionDescriptionToUpdate,
    changesFromPreviousVersion: {
      public: [],
      internal: []
    },
    canCompareWithPreviousVersion
  };
  analyticsChangesToApply.forEach(changeFunction => currentAnalyticsDescription = changeFunction(currentAnalyticsDescription));
  return currentAnalyticsDescription;
}

/**
 * addMetric - add a metric to the analyticsDescription
 *
 * @param {MainAnalyticsCategories} mainCategory - the category that the metric is a part of
 * @param {string} internalMetricName - the name (in the Analytics Document) of the metric to created
 * @param {AnalyticsVersionMetricDescription} metricData - the description of the metric being added
 *
 * @return {AnalyticsVersionUpdater}
 */
const addMetric = (mainCategory, internalMetricName, metricData) => {
  return newAnalyticsVersion => {
    if (newAnalyticsVersion.descriptionOfCategories[mainCategory].metricCalculationDescriptions[internalMetricName]) {
      throw new Error(`Tried to add an analytics metric when the metric already exists ${mainCategory}.${internalMetricName}`);
    }
    return {
      ...newAnalyticsVersion,
      descriptionOfCategories: {
        ...newAnalyticsVersion.descriptionOfCategories,
        [mainCategory]: {
          ...newAnalyticsVersion.descriptionOfCategories[mainCategory],
          metricCalculationDescriptions: {
            ...newAnalyticsVersion.descriptionOfCategories[mainCategory].metricCalculationDescriptions,
            [internalMetricName]: metricData
          }
        }
      },
      changesFromPreviousVersion: {
        internal: [...newAnalyticsVersion.changesFromPreviousVersion.internal, `Added ${mainCategory}.${internalMetricName} metric.`],
        public: [...newAnalyticsVersion.changesFromPreviousVersion.public, `Add new metric: "${metricData.metricTitle}" metric for ${mainCategory}.`]
      }
    };
  };
};

/**
 * removeMetric - remove a metric from the analyticsDescription
 *
 * @param {MainAnalyticsCategories} mainCategory - the category that the metric is a part of
 * @param {string} internalMetricName - the name (in the Analytics Document) of the metric to remove
 * @param {string} reason - the reason the metric is being removed. Should fit into the sentence structure: `Changed because ${reason}`.
 *
 * @return {AnalyticsVersionUpdater}
 */
exports.addMetric = addMetric;
const removeMetric = (mainCategory, internalMetricName, reason) => {
  return newAnalyticsVersion => {
    const originalMetric = newAnalyticsVersion.descriptionOfCategories[mainCategory].metricCalculationDescriptions[internalMetricName];
    if (!originalMetric) {
      throw new Error('Tried to remove a metric that does not exist');
    }
    const updatedCategoryDescription = {
      ...newAnalyticsVersion.descriptionOfCategories[mainCategory],
      metricCalculationDescriptions: {
        ...newAnalyticsVersion.descriptionOfCategories[mainCategory].metricCalculationDescriptions
      }
    };
    delete updatedCategoryDescription.metricCalculationDescriptions[internalMetricName];
    return {
      ...newAnalyticsVersion,
      descriptionOfCategories: {
        ...newAnalyticsVersion.descriptionOfCategories,
        [mainCategory]: updatedCategoryDescription
      },
      changesFromPreviousVersion: {
        internal: [...newAnalyticsVersion.changesFromPreviousVersion.internal, `Removed ${mainCategory}.${internalMetricName} metric because ${reason}.`],
        public: [...newAnalyticsVersion.changesFromPreviousVersion.public, `Removed "${originalMetric.metricTitle}" metric for ${mainCategory} because ${reason}.`]
      }
    };
  };
};

/**
 * deprecateMetric - mark a metric as deprecated
 *
 * @param {MainAnalyticsCategories} mainCategory - the category where the metric is found
 * @param {string} internalMetricName - the name of the metric to mark as deprecated
 * @param {string} reasonForDeprecation - the reason the metric was marked as deprecated. Should fit into the sentence structure: `Deprecated because ${reasonForDeprecation}`.
 *
 * @return {AnalyticsVersionUpdater}
 */
exports.removeMetric = removeMetric;
const deprecateMetric = (mainCategory, internalMetricName, reasonForDeprecation) => {
  return newAnalyticsVersion => {
    const originalMetric = newAnalyticsVersion.descriptionOfCategories[mainCategory].metricCalculationDescriptions[internalMetricName];
    return {
      ...newAnalyticsVersion,
      descriptionOfCategories: {
        ...newAnalyticsVersion.descriptionOfCategories,
        [mainCategory]: {
          ...newAnalyticsVersion.descriptionOfCategories[mainCategory],
          metricCalculationDescriptions: {
            ...newAnalyticsVersion.descriptionOfCategories[mainCategory].metricCalculationDescriptions,
            [internalMetricName]: {
              ...newAnalyticsVersion.descriptionOfCategories[mainCategory].metricCalculationDescriptions[internalMetricName],
              deprecated: true
            }
          }
        }
      },
      changesFromPreviousVersion: {
        internal: [...newAnalyticsVersion.changesFromPreviousVersion.internal, `Deprecated ${mainCategory}.${internalMetricName} because ${reasonForDeprecation}`],
        public: [...newAnalyticsVersion.changesFromPreviousVersion.public, `Deprecated the "${originalMetric.metricTitle}" metric for ${mainCategory} because ${reasonForDeprecation}`]
      }
    };
  };
};

/**
 * updateMetricCalculationDescription - update the calculation description of a metric
 *
 * @param {MainAnalyticsCategories} mainCategory - the category that the metric is a part of
 * @param {string} internalMetricName - the name (in the Analytics Document) of the metric to update the calculation description of
 * @param {string} newMetricCalculationDescription - the new description of how the metric is calculated
 *
 * @return {AnalyticsVersionUpdater}
 */
exports.deprecateMetric = deprecateMetric;
const updateMetricCalculationDescription = (mainCategory, internalMetricName, newMetricCalculationDescription) => {
  return newAnalyticsVersion => {
    const originalMetric = newAnalyticsVersion.descriptionOfCategories[mainCategory].metricCalculationDescriptions[internalMetricName];
    return {
      ...newAnalyticsVersion,
      descriptionOfCategories: {
        ...newAnalyticsVersion.descriptionOfCategories,
        [mainCategory]: {
          ...newAnalyticsVersion.descriptionOfCategories[mainCategory],
          metricCalculationDescriptions: {
            ...newAnalyticsVersion.descriptionOfCategories[mainCategory].metricCalculationDescriptions,
            [internalMetricName]: {
              ...newAnalyticsVersion.descriptionOfCategories[mainCategory].metricCalculationDescriptions[internalMetricName],
              calculationDescription: newMetricCalculationDescription
            }
          }
        }
      },
      changesFromPreviousVersion: {
        internal: [...newAnalyticsVersion.changesFromPreviousVersion.internal, `Changed calculation for ${mainCategory}.${internalMetricName}. Used to be "${originalMetric.calculationDescription}", and is now "${newMetricCalculationDescription}"`],
        public: [...newAnalyticsVersion.changesFromPreviousVersion.public, `The "${originalMetric.metricTitle}" metric for ${mainCategory} is now calculated differently.`]
      }
    };
  };
};
exports.updateMetricCalculationDescription = updateMetricCalculationDescription;